// IMPORTANT: THis file is used on frontend and backend, so do not include
// things here that do not need to be part of the frontend. Instead, use
// the front-end/back-end specific utils files.
import { slugify, wowGameDataSlugify } from '../../../shared/common/slugify';
import { TCharacterRole } from '../../../shared/common/types/common';
import { TRealmResource } from '../../../shared/types/utilityTypes';
import { groupByDictUnique, safeParseInt } from '../../../shared/common/utils';
import { GUILD_REGION_ARMORY_INFO } from '../../../shared/common/character';

// these still use the battlenet domains
export function buildGuildArmoryUrl(regionSlug: string, realmSlug: string, guildName: string) {
  const info = GUILD_REGION_ARMORY_INFO[regionSlug] || GUILD_REGION_ARMORY_INFO.us;
  let cleanedGuildName = guildName;
  if (regionSlug === 'cn') {
    cleanedGuildName = guildName.replace(/\s+\(.*?\)$/, '');
  }
  return `https://${info.domain}/${info.locale}/guild/${regionSlug}/${encodeURIComponent(
    wowGameDataSlugify(realmSlug)
  )}/${encodeURIComponent(slugify(cleanedGuildName))}`;
}

// TODO: link to wowhead instead
export function buildArmoryTalentCalculatorUrl(cls: any, spec: any, talentString: string) {
  const wowCompatibleTalentString = (talentString || '')
    .split('')
    .map((v) => safeParseInt(v, 0) + 1)
    .join('');
  return `https://worldofwarcraft.com/en-us/game/talent-calculator#${cls.slug}/${spec.slug}/talents=${wowCompatibleTalentString}`;
}

export function buildWowheadProfileUrl(name: string) {
  return `https://www.wowhead.com/user=${encodeURIComponent(name)}`;
}

export function buildWarcraftLogsCharacterProfileUrl(regionSlug: string, realmInfo: TRealmResource, name: string) {
  let realmSlug = realmInfo.slug;
  if (['ru_RU', 'zh_TW', 'zh_CN', 'ko_KR'].indexOf(realmInfo.locale) !== -1) {
    realmSlug = realmInfo.altSlug;
    // from this support request:
    // If the player is from one of the four Russian realms that have
    // character "й" in their names (Ревущий Фьорд, Черный Шрам, Разувий, Борейская Тундра),
    // the generated Warcraftlogs link will be invalid. For it to be valid, character "й"
    // has to be replaced by "и".
    realmSlug = realmSlug.replace(/й/g, 'и');
  }
  return `https://www.warcraftlogs.com/character/${regionSlug}/${realmSlug}/${name}`;
}

export function buildRaidbotsCharacterProfileUrl(regionSlug: string, realmInfo: TRealmResource, name: string) {
  let realmSlug = realmInfo.slug;
  if (['zh_TW', 'zh_CN', 'ko_KR'].indexOf(realmInfo.locale) !== -1) {
    realmSlug = realmInfo.altSlug;
  }
  return `https://www.raidbots.com/simbot/quick?region=${regionSlug}&realm=${realmSlug}&name=${name}`;
}

export function buildWarcraftLogsGuildProfileUrl(regionSlug: string, realmInfo: TRealmResource, name: string) {
  let realmSlug = realmInfo.slug;
  if (['ru_RU', 'zh_TW', 'zh_CN', 'ko_KR'].indexOf(realmInfo.locale) !== -1) {
    realmSlug = realmInfo.altSlug;
    // from this support request:
    // If the player is from one of the four Russian realms that have
    // character "й" in their names (Ревущий Фьорд, Черный Шрам, Разувий, Борейская Тундра),
    // the generated Warcraftlogs link will be invalid. For it to be valid, character "й"
    // has to be replaced by "и".
    realmSlug = realmSlug.replace(/й/g, 'и');
  }
  return `https://www.warcraftlogs.com/guild/${regionSlug}/${realmSlug}/${name}`;
}

// Blizzard changed the reset time on November 16th:
//
// Weekly Reset Time Changing to 05:00 CET on 16 November
//
// https://eu.forums.blizzard.com/en/wow/t/weekly-reset-time-changing-to-0500-cet-on-16-november/398498

// FIXME: send this up from server
export const CLASSES_BY_ID: any = {
  1: {
    name: 'Warrior',
    slug: 'warrior',
    color: '#c79c6e'
  },
  2: {
    name: 'Paladin',
    slug: 'paladin',
    color: '#f58cba'
  },
  3: {
    name: 'Hunter',
    slug: 'hunter',
    color: '#abd473'
  },
  4: {
    name: 'Rogue',
    slug: 'rogue',
    color: '#fff569'
  },
  5: {
    name: 'Priest',
    slug: 'priest',
    color: '#fff'
  },
  6: {
    name: 'Death Knight',
    slug: 'death-knight',
    color: '#c41f3b'
  },
  7: {
    name: 'Shaman',
    slug: 'shaman',
    color: '#0070de'
  },
  8: {
    name: 'Mage',
    slug: 'mage',
    color: '#69ccf0'
  },
  9: {
    name: 'Warlock',
    slug: 'warlock',
    color: '#9482c9'
  },
  10: {
    name: 'Monk',
    slug: 'monk',
    color: '#00ff96'
  },
  11: {
    name: 'Druid',
    slug: 'druid',
    color: '#ff7d0a'
  },
  12: {
    name: 'Demon Hunter',
    slug: 'demon-hunter',
    color: '#a330c9'
  },
  13: {
    name: 'Evoker',
    slug: 'evoker',
    color: '#33937f'
  }
};

export function infoForClass(classId: number): any {
  return CLASSES_BY_ID[classId];
}

// FIXME: send this up from server
const ROLE_CLASS_IDS: any = {
  tank: [1, 2, 6, 10, 11, 12],
  healer: [2, 5, 7, 10, 11, 13]
};

export function canPerformRole(classId: number, role: TCharacterRole) {
  if (ROLE_CLASS_IDS[role]) {
    return ROLE_CLASS_IDS[role].indexOf(classId) !== -1;
  }
  return true;
}

// TODO: this should mostly be a fallback for pulling guild logos, may want to remove in the future so we don't have to update this slug each tier.
const CURRENT_RWF_RAID_SLUG = 'manaforge-omega';

const GUILD_ID_INFO = [
  // Echo
  {
    id: 1047044,
    color: '#f8d78e', // was ff4e00
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/echo/logo.png`
  },
  // Liquid
  {
    id: 1712677,
    color: '#ffffff',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/liquid/guildlogo.png`
  },
  // // Echo - for XCC TR event
  // {
  //   id: 1739612,
  //   color: '#cd1d2d',
  //   logo: 'https://cdn-era.raiderio.net/images/rwf/sepulcher-of-the-first-ones/echo/guildlogo.png`
  // },
  // // Liquid - for XCC TR event
  // {
  //   id: 1803240,
  //   color: '#ffffff',
  //   logo: 'https://cdn-era.raiderio.net/images/rwf/sepulcher-of-the-first-ones/liquid/guildlogo.png`
  // },
  // Pieces
  {
    id: 1727102,
    color: '#4498cb',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/pieces/guildlogo.png`
  },
  // FatSharkYes',
  {
    id: 1213,
    color: '#0090d3',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/fatsharkyes/logo.png`
  },
  // Skyline
  {
    id: 1866222,
    color: '#c30d23',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/skyline/logo.png`
  },
  // JTH
  {
    id: 847774,
    color: '#ff3332',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/jth/logo.png`
  },
  // Method
  {
    id: 316123,
    color: '#ff9015',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/method/logo.png`
  },
  // Exorsus
  {
    id: 21308,
    color: '#9e7530',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/exorsus/logo.png`
  },
  // BDG
  {
    id: 846835,
    color: '#a02c2f',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/bdg/logo.png`
  },
  // Aversion
  {
    id: 203618,
    color: '#9e9e9e',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/aversion/logo.png`
  },
  // Alpha
  {
    id: 899776,
    color: '#25799e',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/alpha/logo.png`
  },
  // Club Camel,
  {
    id: 6585,
    color: '#b9a25e',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/club-camel/logo.png`
  },
  // Imperative
  {
    id: 1115725,
    color: '#04abe4',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/imperative/logo.png`
  },
  // The Early Shift
  {
    id: 30591,
    color: '#b2202d',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/the-early-shift/logo.png`
  },
  // Aster - #1568876
  {
    id: 1568876,
    color: '#ffffff',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/aster/logo.png`
  },
  // Instant Dollars
  {
    id: 1916,
    color: '#32c654',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/instant-dollars/logo.png`
  },
  // Vesper
  {
    id: 1175663,
    color: '#ffffff',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/vesper/logo.png`
  },
  // Chaojie / Huoguo
  {
    id: 2058900,
    color: '#f45d0e',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/huoguo/logo.png`
  },
  // KeaHoarl
  {
    id: 560287,
    color: '#1c74ba',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/keahoarl/logo.png`
  },
  // Wings
  {
    id: 1175973,
    color: '#bbb3ae',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/wings/logo.png`
  },
  // Exposed
  {
    id: 138698,
    color: '#02739f',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/exposed/logo.png`
  },
  // Honestly
  {
    id: 2387,
    color: '#144282',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/honestly/logo.png`
  },
  // Honolulu
  {
    id: 42849,
    color: '#144282',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/honolulu/logo.png`
  }, // Northern Sky
  {
    id: 656001,
    color: '#04f9f9',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/northern-sky/logo.png`
  },
  // poptartcorndoG
  {
    id: 1133352,
    color: '#fa9ccc',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/poptartcorndog/logo.png`
  },
  // Stay Mad
  {
    id: 733010,
    color: '#7d4a2d',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/stay-mad/logo.png`
  },
  // spk
  {
    id: 5834,
    color: '#b7647a',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/spk/logo.png`
  },
  // xd
  {
    id: 649287,
    color: '#0e7a8b',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/xd/logo.png`
  },
  // LEVELS
  {
    id: 1649324,
    color: '#b47c13',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/levels/logo.png`
  },
  // Project
  {
    id: 567858,
    color: '#8a8b8a',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/project/logo.png`
  },
  // Ethical
  {
    id: 909517,
    color: '#c02b32',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/ethical/logo.png`
  },
  // Melee Mechanics
  {
    id: 558946,
    color: '#c48709',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/melee-mechanics/logo.png`
  },
  // Nerd Crew
  {
    id: 1100476,
    color: '#e0b559',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/nerd-crew/logo.png`
  },
  // Revolutionist
  {
    id: 1390863,
    color: '#fafac3',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/revolutionist/logo.png`
  },
  // Tony Halme Pro Skater
  {
    id: 1060875,
    color: '#ed262a',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/tony-halme/logo.png`
  },
  // Blue Shoe (End Myth US Guild)
  {
    id: 249848,
    color: '#2596be',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/blue-shoe/logo.png`
  },
  // End Myth
  {
    id: 912713,
    color: '#17d0ea',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/end-myth/logo.png`
  },
  // ZboX
  {
    id: 2058020,
    color: '#00a0e9',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/zbox/logo.png`
  },
  {
    id: 1493095,
    color: '#2596be',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/laget/logo.png`
  },
  {
    id: 531686,
    color: '#d52c4c',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/dmg/logo.png`
  },
  {
    id: 542651,
    color: '#689ef6',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/synergy/logo.png`
  },
  {
    id: 43183,
    color: '#29a9db',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/nascent/logo.png`
  },
  {
    id: 2391,
    color: '#46d99e',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/bound/logo.png`
  },
  {
    id: 6331,
    color: '#d5ad42',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/consequence/logo.png`
  },
  {
    id: 750617,
    color: '#ffffff',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/advance/logo.png`
  },
  {
    id: 1700701,
    color: '#451819',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/wrath-of-anarchy/logo.png`
  },
  {
    id: 1751394,
    color: '#ffffff',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/banhammer/logo.png`
  },
  {
    id: 43866,
    color: '#ffffff',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/blur/logo.png`
  },
  {
    id: 9276,
    color: '#6e6bf2',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/rain/logo.png`
  },
  {
    id: 606245,
    color: '#f01414',
    logo: `https://cdn.raiderio.net/images/rwf/${CURRENT_RWF_RAID_SLUG}/mate/logo.png`
  }
];

const guildMetadata = groupByDictUnique(GUILD_ID_INFO, 'id');

export function getGuildLogo(guildId: number): string | null {
  return guildMetadata[guildId]?.logo || null;
}

export function getGuildColor(guildId: number): string | null {
  return guildMetadata[guildId]?.color || null;
}

export function resolveGuildBrandColor(
  guildId: number,
  groupCustomization?: { brand_color?: string | null } | null
): string | null {
  if (groupCustomization?.brand_color) {
    return groupCustomization.brand_color;
  }
  // If a customization record exists and brand_color is explicitly null,
  // the guild cleared their color — don't fall back to the hardcoded list.
  if (groupCustomization && 'brand_color' in groupCustomization) {
    return null;
  }
  return getGuildColor(guildId);
}
